import React from 'react'
import { SimpleUser, User } from '../../../providers/context'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { IconAngleDown, IconCalendarCheck, IconGlobe, IconPrint, IconScheduled } from '../../../icons/library'
import { BlockCalendar } from '../components/blockCalendar'
import { SingleUserSelector } from '../../../components/userSelector/userSelector'
import { Select } from '../../../components/form/select'
import { isMobileSize, pluralize } from '../../../utils'
import { isThisYear, parseISO } from 'date-fns'
import { format, formatInTimeZone } from 'date-fns-tz'
import {
  ConferenceAppointment,
  TeacherConferenceData,
  ConferenceTime,
  ConferenceLocationEdit,
  SharedConferenceData,
  ConferenceLocation,
  SimpleUserWithLocation,
} from '../components/conferenceComponents'
import { ControlledModal, ModalTitle } from '../../../layouts/components/modal'
import { parseISOWithTimezoneFallback } from '../../../components/simple/date'
import { Divider } from '../../../components/simple/divider'
import { Button, ButtonStyle } from '../../../components/simple/button'
import { LabeledCheckbox } from '../../../components/form/checkbox'
import { Toasts } from '../../../providers/toast'
import { FloatingLoader } from '../../../components/simple/loader'
import { Popups } from '../../../providers/popups'
import { useLanguages } from '../../../providers/languages'
import { useTranslation } from '../../../providers/translation'

type ConferenceStudentSearchResult = SimpleUser & { defaultLanguage?: string }

export type ScheduleRequest = {
  time: ConferenceTime
  language?: { name: string; value: string }
  student: SimpleUser
  teacher?: SimpleUserWithLocation
  location: ConferenceLocation
}

export const ScheduleTab: React.FC<{
  conference: TeacherConferenceData
  viewAsAdmin: boolean
  user: User
  editYourLocation: () => void
}> = ({ conference, viewAsAdmin, user, editYourLocation }) => {
  const days = React.useMemo(() => conference.days.map((d) => parseISO(d)), [conference.days])
  const [selectedDay, setSelectedDay] = React.useState(days[0])
  const [student, setStudent] = React.useState<ConferenceStudentSearchResult | undefined>(undefined)
  const [rawTeacher, setTeacher] = React.useState<SimpleUserWithLocation | undefined>(undefined)
  const teacher = viewAsAdmin ? rawTeacher : undefined
  const languages = useLanguages()
  const languageOptions = React.useMemo(
    () => [
      {
        name: 'Any Language',
        value: '',
      },
      ...languages.getOptions((l) => l.lexikeetCode !== undefined),
    ],
    [languages]
  )
  const [rawLanguage, setLanguage] = React.useState(languageOptions[0])
  const language = rawLanguage.value === '' ? undefined : rawLanguage

  const { data: stats, reload: reloadStats } = useRequest<{ appointmentsPerDay: { date: string; count: number }[] }>(
    '/frontend_api/getConferenceStats',
    React.useMemo(
      () => ({
        id: conference.id,
        viewAsAdmin,
      }),
      [conference.id, viewAsAdmin]
    )
  )
  const appointmentsPerDay = React.useMemo(
    () =>
      stats?.appointmentsPerDay.map((day) => ({
        ...day,
        parsedDate: parseISO(day.date),
      })),
    [stats]
  )

  const todayAppointmentCount =
    appointmentsPerDay?.find((day) => day.parsedDate.getTime() === selectedDay.getTime())?.count ?? 0
  const totalAppointments = appointmentsPerDay?.reduce((acc, day) => acc + day.count, 0)

  const {
    data: schedule,
    reload: reloadScheduleOnly,
    loading: loadingSchedule,
  } = useRequest<{
    times: ConferenceTime[]
    existingAppointmentTimes?: ConferenceTime[]
  }>(
    '/frontend_api/getConferenceTimes',
    React.useMemo(
      () => ({
        conferenceID: conference.id,
        day: format(selectedDay, 'y-MM-dd'),
        viewAsAdmin,
        language: language?.value,
        studentID: student?.id,
        teacherID: viewAsAdmin ? teacher?.id : undefined,
      }),
      [conference.id, selectedDay, viewAsAdmin, language, student, teacher]
    )
  )

  const reloadSchedule = () => {
    reloadScheduleOnly()
    reloadStats()
  }

  const [scheduleRequest, setScheduleRequest] = React.useState<ScheduleRequest | undefined>(undefined)

  const userSearchData = React.useMemo(() => ({ conferenceID: conference.id }), [conference.id])

  return (
    <div className="mt-2 flex min-h-0 flex-grow gap-4">
      {scheduleRequest && (
        <ScheduleRequestModal
          conference={conference}
          scheduleRequest={scheduleRequest}
          onClose={() => setScheduleRequest(undefined)}
          onSchedule={reloadSchedule}
          user={user}
        />
      )}
      <div className="flex max-w-[315px] flex-col gap-4 overflow-auto">
        <BlockCalendar days={days} selectedDay={selectedDay} onSelectDay={setSelectedDay} />
        {appointmentsPerDay && (
          <div className="flex flex-col gap-1">
            <div className="font-semibold">{totalAppointments} total appointments</div>
            {appointmentsPerDay.map((day) => (
              <div key={day.date} className="ml-2">
                {isThisYear(day.parsedDate)
                  ? format(day.parsedDate, 'MMM do', { timeZone: conference.timezone })
                  : format(day.parsedDate, 'MMM do, yyyy', { timeZone: conference.timezone })}
                : <b>{day.count}</b>
              </div>
            ))}
          </div>
        )}
        {!viewAsAdmin && <ConferenceLocationEdit conference={conference} editYourLocation={editYourLocation} />}
      </div>
      <div className="print-full flex min-w-0 max-w-[800px] flex-grow flex-col gap-2">
        <div className="print-only text-center text-lg font-bold">
          {viewAsAdmin ? conference.name : `${user.name}'s Schedule`}
        </div>
        <div className="print-hide flex items-center gap-2">
          <b className="mr-2 text-nowrap">Make an Appointment</b>
          <SingleUserSelector
            placeholder="Select Student"
            userDescriptor="students"
            selectedUser={student}
            setSelectedUser={(s) => {
              setStudent(s)
              setLanguage((l) => languageOptions.find((l) => l.value === (s?.defaultLanguage ?? '')) ?? l)
            }}
            url="/frontend_api/studentConferenceSearch"
            searchData={userSearchData}
          />
          {conference.multilingual && (
            <Select
              options={languageOptions}
              value={rawLanguage}
              onChange={setLanguage}
              className={`rounded-[16.8px] border border-gray-200 !py-[5.25px] text-[13px] ${language === undefined ? 'text-gray-400' : ''}`}
              arrow={<IconAngleDown size="sm" className="mr-1 text-gray-300" />}
            />
          )}
          {viewAsAdmin && (
            <SingleUserSelector
              placeholder="Select Teacher"
              userDescriptor="teachers"
              selectedUser={teacher}
              setSelectedUser={setTeacher}
              url="/frontend_api/teacherConferenceSearch"
              searchData={userSearchData}
            />
          )}
        </div>
        {schedule?.existingAppointmentTimes && (
          <div className="flex flex-col gap-3 text-nowrap rounded-[12.5px] bg-lightBlue p-3 shadow-even-inset">
            <div className="-mb-2 font-semibold text-charcoal-text">Already Scheduled</div>
            {schedule.existingAppointmentTimes.map((time) => (
              <div
                key={time.id}
                className="flex items-center justify-between gap-2 rounded-[5px] bg-white p-3 shadow-even"
              >
                <b>
                  {formatInTimeZone(
                    parseISOWithTimezoneFallback(time.date),
                    conference.timezone,
                    'MMM d, yyyy, h:mm a'
                  )}
                </b>
                <ConferenceAppointment
                  conference={conference}
                  appointment={time.appointments[0]}
                  user={user}
                  className="bg-slate-50"
                  reloadSchedule={reloadSchedule}
                />
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 text-center text-lg font-bold">
          {format(selectedDay, 'MMM, d, yyyy', { timeZone: conference.timezone })} ({todayAppointmentCount}{' '}
          {pluralize(todayAppointmentCount, 'Appointment')})
          <IconPrint opacity={0.25} className="ml-2 cursor-pointer" onClick={() => window.print()} tooltip="Print" />
        </div>
        <div
          className={`flex flex-col gap-3 overflow-auto rounded-[12.5px] bg-lightBlue p-3 shadow-even-inset ${loadingSchedule ? 'pointer-events-none opacity-50' : ''}`}
        >
          {schedule?.times.map((time) => (
            <ConferenceTime
              key={time.date}
              conference={conference}
              time={time}
              viewAsAdmin={viewAsAdmin}
              viewAsOtherTeacher={viewAsAdmin ? teacher : undefined}
              user={user}
              reloadSchedule={reloadSchedule}
              scheduleAppointment={() =>
                setScheduleRequest({
                  time,
                  language: language?.value === 'en' ? undefined : language,
                  teacher: viewAsAdmin ? teacher : undefined,
                  student: student!,
                  location: viewAsAdmin ? teacher!.location : conference.yourLocation,
                })
              }
              selectedLanguage={language?.value === 'en' ? undefined : language}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export const ScheduleRequestModal: React.FC<{
  conference: SharedConferenceData
  scheduleRequest: ScheduleRequest
  onClose: () => void
  onSchedule: () => void
  user: User
  showConfirmation?: boolean
  confirmationActions?: React.ReactNode
}> = ({ conference, scheduleRequest, onClose, onSchedule, user, showConfirmation, confirmationActions }) => {
  const [loading, setLoading] = React.useState(false)
  const locationOptions = React.useMemo(
    () =>
      [
        { name: 'In Person', value: 'inperson' },
        { name: 'Virtual', value: 'virtual' },
      ].filter((o) => o.value === conference.virtualOrInPerson || conference.virtualOrInPerson === 'both') as {
        name: string
        value: 'inperson' | 'virtual'
      }[],
    []
  )
  const [location, setLocation] = React.useState(locationOptions[0])

  const { data: splitConferenceData } = useRequest<{
    force_multiple: boolean
    possible_guardians: { id: number; name: string }[]
  }>(
    '/ptc/checkMultiple',
    React.useMemo(
      () => ({
        teacher_id: scheduleRequest.teacher?.id,
        student_id: scheduleRequest.student?.id,
        time_id: scheduleRequest.time.id,
      }),
      [scheduleRequest]
    )
  )
  const [dontShare, setDontShare] = React.useState(false)
  const showGuardianOptions = user.type === 'teacher'
  const guardianOptions = React.useMemo(
    () => splitConferenceData?.possible_guardians.map((g) => ({ value: g.id, name: g.name })),
    [splitConferenceData]
  )
  const [selectedGuardian, setSelectedGuardian] = React.useState(guardianOptions?.[0])
  React.useEffect(() => {
    if (!splitConferenceData || !guardianOptions) return
    if (splitConferenceData?.force_multiple) setDontShare(true)
    if (guardianOptions.length === 0) {
      Toasts.showToast({ message: 'This conference is already scheduled with all guardians.', error: true })
      onClose()
    } else setSelectedGuardian(guardianOptions[0])
  }, [splitConferenceData])

  const [done, setDone] = React.useState(false)

  const schedule = () => {
    setLoading(true)
    makeRequest<string>('/ptc/addappointment', {
      time_id: scheduleRequest.time.id,
      student_id: scheduleRequest.student?.id,
      teacher_id: scheduleRequest.teacher?.id,
      language: scheduleRequest.language?.value,
      virtual_or_in_person: location.value,
      multiple: dontShare,
      guardian_id: dontShare && showGuardianOptions ? selectedGuardian?.value : undefined,
    })
      .then((response) => {
        if (response) Popups.simplePopup(response)
        else {
          if (!isMobileSize)
            Toasts.showToast({ message: 'Appointment has been scheduled and this family has been notified.' })
          onSchedule()
          if (!showConfirmation) onClose()
          else setDone(true)
        }
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  const { tr } = useTranslation()

  return (
    <ControlledModal
      open={true}
      disabled={loading}
      showLoader={loading}
      onOpenChange={(o) => !o && onClose()}
      size="sm"
    >
      {done && (
        <div className="flex h-[60px] w-[60px] items-center justify-center self-center rounded-full bg-blue-300 text-blue-500">
          <IconCalendarCheck size="2x" />
        </div>
      )}
      {done && <div className="self-center font-bold text-blue-500">{tr('Appointment Scheduled')}</div>}
      <ModalTitle className="mt-2 self-center text-lg font-bold">
        {scheduleRequest.student.name}
        {scheduleRequest.teacher && <> + {scheduleRequest.teacher.name}</>}
      </ModalTitle>
      <div className="self-center text-lg"></div>
      <div className="text-center">
        {formatInTimeZone(
          parseISOWithTimezoneFallback(scheduleRequest.time.date),
          conference.timezone,
          'MMM d, yyyy, h:mm a'
        )}{' '}
        ({conference.duration} {tr('minutes')})
      </div>
      {scheduleRequest.language && (
        <b className="self-center">
          <IconGlobe /> {scheduleRequest.language.name}
        </b>
      )}

      {locationOptions.length > 1 && !done && (
        <Select
          className="mt-2 !w-auto self-center"
          options={locationOptions}
          value={location}
          onChange={setLocation}
        />
      )}
      <ConferenceLocation
        className="self-center"
        virtualOrInPerson={location.value}
        location={scheduleRequest.location}
      />
      {dontShare && !done && showGuardianOptions && <Divider className="mt-2" />}
      {done ? (
        dontShare &&
        selectedGuardian && (
          <div className="self-center">
            {tr('Only for:')} {selectedGuardian.name}
          </div>
        )
      ) : (
        <>
          <LabeledCheckbox
            className="self-center"
            label={
              user.type === 'teacher'
                ? "Don't share conference with all guardians"
                : tr("Don't share with other guardians")
            }
            checked={dontShare}
            onChecked={setDontShare}
            disabled={splitConferenceData?.force_multiple}
            tooltip={
              splitConferenceData?.force_multiple
                ? 'Other guardians have selected to have individual conferences. They must delete their existing conferences to create a shared conference.'
                : undefined
            }
          />
          {dontShare && guardianOptions && selectedGuardian && showGuardianOptions && (
            <div className="flex flex-col items-center gap-2 self-center">
              <b>Select a guardian for this conference:</b>{' '}
              <Select
                className="!w-auto"
                options={guardianOptions}
                onChange={setSelectedGuardian}
                value={selectedGuardian}
              />
            </div>
          )}
        </>
      )}
      <div className="mt-2 flex justify-center gap-2">
        {done ? (
          (confirmationActions ?? <Button onClick={onClose}>{tr('Close')}</Button>)
        ) : (
          <>
            <Button onClick={onClose}>{tr('Cancel')}</Button>
            <Button
              buttonStyle={ButtonStyle.blue}
              onClick={schedule}
              disabled={loading || splitConferenceData === undefined}
            >
              {tr('Schedule')}
            </Button>
          </>
        )}
      </div>
    </ControlledModal>
  )
}
